<template>
  <div class="file-tree-container">
    <div class="sidebar-header">
      <h3>资源管理器</h3>
      <div class="toolbar-container">
        <div class="toolbar-item" @click="changeWorkDir">
          <el-tooltip content="切换工作目录" placement="bottom" effect="light">
            <i class="el-icon-sort"></i>
          </el-tooltip>
        </div>
        <div class="toolbar-item" @click="triggerUpload">
          <el-tooltip content="上传文件" placement="bottom" effect="light">
            <i class="el-icon-upload"></i>
          </el-tooltip>
        </div>
        <div class="toolbar-item" @click="createNewFolder">
          <el-tooltip content="新建文件夹" placement="bottom" effect="light">
            <i class="el-icon-folder-add"></i>
          </el-tooltip>
        </div>
        <div class="toolbar-item" @click="createNewFile">
          <el-tooltip content="新建文件" placement="bottom" effect="light">
            <i class="el-icon-document-add"></i>
          </el-tooltip>
        </div>
        <div class="toolbar-item" @click="refreshFileList">
          <el-tooltip content="刷新资源列表" placement="bottom" effect="light">
            <i class="el-icon-refresh"></i>
          </el-tooltip>
        </div>
      </div>      
    </div>

    <div class="file-tree" @click="handleBlankAreaClick">
      <!-- 文件树递归渲染 -->
      <file-tree-item
        v-for="(file, index) in fileList"
        :key="file.name + '_' + index"
        :file="file"
        :expanded-folders="expandedFolders"
        :current-dir="rootDir"
        @select="selectFile"
        @toggle="toggleFolder"
        @download="handleDownload"
        @rename="handleRename"
        @delete="handleDelete"
        @refresh="refreshFileList"
        @refresh-folder="refreshFolder"
      />
      
      <div v-if="fileList.length === 0 && !creatingFolder && !creatingFile" class="empty-state">
        <i>📁</i>
        <p>暂无文件</p>
        <p>点击上方按钮上传文件</p>
      </div>
      
      <!-- 添加：可点击的填充区域 -->
      <div class="clickable-fill-area"></div>
    </div>
    <!-- 新建文件夹模态框 -->
    <div v-if="creatingFolder" class="modal-overlay" @click="cancelCreateFolder">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <svg class="modal-icon" viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M19.35,10.04C18.67,6.59 15.64,4 12,4C9.11,4 6.6,5.64 5.35,8.04C2.34,8.36 0,10.91 0,14C0,17.31 2.69,20 6,20H19C21.76,20 24,17.76 24,15C24,12.36 21.95,10.22 19.35,10.04Z"/>
          </svg>
          <h3>新建文件夹</h3>
        </div>
        <div class="modal-body">
          <input
            ref="folderInput"
            v-model="newFolderName"
            class="modal-input"
            placeholder="请输入文件夹名称"
            @keyup.enter="confirmCreateFolder"
            @keyup.esc="cancelCreateFolder"
            autofocus
          />
          <div v-if="folderNameError" class="error-message">
            {{ folderNameError }}
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-btn cancel-btn" @click="cancelCreateFolder">
            <span>取消</span>
          </button>
          <button 
            class="modal-btn confirm-btn" 
            @click="confirmCreateFolder"
            :disabled="!!folderNameError || !newFolderName.trim()"
          >
            <span>确定</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 新建文件模态框 -->
    <div v-if="creatingFile" class="modal-overlay" @click="cancelCreateFile">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <svg class="modal-icon" viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M19.35,10.04C18.67,6.59 15.64,4 12,4C9.11,4 6.6,5.64 5.35,8.04C2.34,8.36 0,10.91 0,14C0,17.31 2.69,20 6,20H19C21.76,20 24,17.76 24,15C24,12.36 21.95,10.22 19.35,10.04Z"/>
          </svg>
          <h3>新建文件</h3>
        </div>
        <div class="modal-body">
          <input
            ref="fileInputName"
            v-model="newFileName"
            class="modal-input"
            placeholder="请输入文件名称"
            @keyup.enter="confirmCreateFile"
            @keyup.esc="cancelCreateFile"
            autofocus
          />
          <div v-if="fileNameError" class="error-message">
            {{ fileNameError }}
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-btn cancel-btn" @click="cancelCreateFile">
            <span>取消</span>
          </button>
          <button 
            class="modal-btn confirm-btn" 
            @click="confirmCreateFile"
            :disabled="!!fileNameError || !newFileName.trim()"
          >
            <span>确定</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 文件上传组件 -->
    <file-upload
      ref="fileUploadComponent"
      :target-dir="currentSelectedDir"
      :accept="'.md,.markdown,.txt,.pdf,image/*'"
      :compress-images="true"
      :max-size-mb="10"
      @upload-success="onUploadSuccess"
    />
    
    <!-- 文件下载组件 -->
    <file-download
    ref="fileDownloadComponent"
    />

  </div>
</template>

<script>
import '@/css/FileTree.css';
import { 
        file_list_path,
        file_delete_path,
        file_mkdir_path,
        file_save_path,
        file_rename_path,
        file_move_path,
      } from '@/js/api_paths';
import FileTreeItem from './FileTreeItem.vue';
import FileUpload from './FileUpload.vue'; // 引入文件上传组件
import FileDownload from './FileDownload.vue'; // 引入文件下载组件

import { getParentDirectory,
         validateFileName,
         validateFolderName,
         getName
} from '@/js/tools';

export default {
  name: 'FileTree',
  components: {
    FileTreeItem,
    FileUpload,
    FileDownload
  },
  props: {
  },
  data(){
    return {
      creatingFolder: false,
      creatingFile: false,
      newFolderName: '',
      newFileName: '',
      fileList: [],
      folderNameError: '', // 文件夹名称错误信息
      fileNameError: ''    // 文件名称错误信息
    }
  },
  computed :{
    expandedFolders(){
      return this.$store.state.expandedFolders; // 用于存储展开的文件夹路径
    },
    rootDir(){ // 当前根目录
      return this.$store.state.rootDir;
    },
    currentFilePath(){ // 当前选中的文件
      return this.$store.state.currentFilePath;
    },
    currentSelectedDir(){ // 当前选中的目录
      return this.$store.state.currentSelectedDir;
    },
    moveSignal(){ // 移动文件的信号
      return this.$store.state.moveSignal;
    }
  },
  watch: {
    rootDir(newVal, oldVal){
      if(newVal != oldVal){
        this.refreshFileList();
      }
    },
    moveSignal(){ // 监听是否有文件移动信号
      this.handleMove();
    },
    // 监听文件夹名称变化，实时验证
    newFolderName(newVal) {
      this.folderNameError = validateFolderName(newVal);
    },
    // 监听文件名称变化，实时验证
    newFileName(newVal) {
      this.fileNameError = validateFileName(newVal);
    }
  },

  mounted(){
    if(this.$store.state.is_login){
      this.refreshFileList();
    }
  },

  methods: {
    // 切换工作目录
    changeWorkDir(){
      this.$store.commit('SET_SHOW_SELECT_DIR_MODAL', true);
    },

    // 刷新当前根目录下的文件项目
    refreshFileList(){
      this.fileList = [];
      const token = this.$store.state.token;
      let req = {
        "relative_dir": this.rootDir
      };
      fetch(file_list_path, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(req)
      })
      .then(response => {
        return response.json();
      })
      .then(data => {
        if(data["status"] == "success"){
          this.fileList = data["msg"];
        }
      })
      .catch(error => {
        console.log("wsh");
        console.log(error);
      })
    },

    // 刷新指定文件夹的内容
    async refreshFolder(folderPath) {
      try {
        const token = this.$store.state.token;
        const req = {
          "relative_dir": folderPath
        };
        
        const response = await fetch(file_list_path, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(req)
        });
        
        const data = await response.json();
        if (data.status === "success") {
          return data.msg;
        }
      } catch (error) {
        console.error('刷新文件夹失败:', error);
      }
      return [];
    },

    // 触发本地文件选择框
    triggerUpload() {
      this.$refs.fileUploadComponent.triggerUpload();
    },

    // 上传文件成功
    onUploadSuccess() {
      if(this.currentSelectedDir == this.rootDir){ // 如果当前选择的目录就是当前根目录, 则需要以不一样的方式来刷新
        this.refreshFileList();
      }
      else{
        this.$store.commit('CHANGE_FOLDER_REFRESH_FLAG', this.currentSelectedDir); // 刷新当前选中的文件夹
      }
    },

    // 选中某个文件
    selectFile(msg) {
      if(this.$store.state.isLoadingContent){ 
        this.$message?.warning('正在加载文件内容，请稍候...');
        return;
      }
      // filePath 是完整的文件路径，直接使用
      this.$store.commit('SET_CURRENT_SELECTED_DIR', msg.currentDir);
      this.$store.commit('SET_CURRENT_FILE_PATH', msg.filePath);
      this.$store.commit('SET_CURRENT_SELECTED_TYPE', 'file'); // 当前选中的是文件
    },

    // 文件夹展开/收起控制
    toggleFolder(folderPath) {
      this.$store.commit("CHANGE_EXPANDED_FOLDERS", folderPath);
      this.$store.commit('SET_CURRENT_SELECTED_DIR', folderPath);
      this.$store.commit('SET_CURRENT_SELECTED_TYPE', 'dir'); // 当前选中的是文件夹
    },


    // --- 菜单操作处理函数 ---
    async handleDownload(fullPath) { // 下载当前选中目录下的文件
      // 直接调用子组件的方法，传入文件路径
      if (this.$refs.fileDownloadComponent) {
        this.$refs.fileDownloadComponent.triggerDownload(fullPath);
      } else {
        console.error('FileDownload 组件未找到');
      }
    },

    async handleRename(msg) {
      try {
          const token = this.$store.state.token;
          if (!token) {
            this.$message({
              type: 'error',
              message: '请先登录',
              duration: 1500
            });
            return;
          }

          // 构造请求体
          const req = {
            "old_path": msg.oldPath,
            "new_path": msg.newPath
          };

          // 发送 POST 请求重命名文件/目录
          const response = await fetch(file_rename_path, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(req)
          });

          const data = await response.json();

          if (data.status === "success") {
            this.$message({
              type: 'success',
              message: '重命名成功',
              duration: 1500
            });
            
            if(msg.currentDir == this.rootDir){ // 刷新当前工作目录
              this.refreshFileList();
            }
            else{
              this.$store.commit('CHANGE_FOLDER_REFRESH_FLAG', msg.currentDir);
            } 
          } else {
            const errorMsg = data.msg || '重命名失败';
            this.$message({
              type: 'error',
              message: errorMsg,
              duration: 1500
            });
          }

        } catch (error) {
          console.error('重命名时出错:', error);
          this.$message({
            type: 'error',
            message: '重命名失败: ' + error.message,
            duration: 1500
          });
        } 
    },

    async handleMove() {
      try {
        const token = this.$store.state.token;
        if (!token) {
          this.$message({
            type: 'error',
            message: '请先登录',
            duration: 1500
          });
          return;
        }

        const source_path = this.$store.state.sourcePath;
        const target_dir = this.$store.state.targetDir;
        
        // 检查必要参数
        if (!source_path || !target_dir) {
          this.$message({
            type: 'error',
            message: '缺少必要参数',
            duration: 1500
          });
          return;
        }

        // 构造请求体
        const req = {
          "source_path": source_path,
          "destination_dir": target_dir
        };

        // 发送 POST 请求移动文件/目录
        const response = await fetch(file_move_path, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(req)
        });

        const data = await response.json();
        console.log(data);

        if (data.status === "success") {
          this.$message({
            type: 'success',
            message: '移动成功',
            duration: 1500
          });
          
          // 刷新相关目录
          // 刷新源路径的父目录
          const sourceParentDir = getParentDirectory(source_path);
          if (sourceParentDir === this.rootDir) {
            this.refreshFileList();
          } else {
            this.$store.commit('CHANGE_FOLDER_REFRESH_FLAG', sourceParentDir);
          }
          
          // 刷新目标目录
          if (target_dir === this.rootDir) {
            this.refreshFileList();
          } else {
            this.$store.commit('CHANGE_FOLDER_REFRESH_FLAG', target_dir);
          }
          
        } else {
          const errorMsg = data.msg || '移动失败';
          this.$message({
            type: 'error',
            message: errorMsg,
            duration: 1500
          });
        }

      } catch (error) {
        console.error('移动时出错:', error);
        this.$message({
          type: 'error',
          message: '移动失败: ' + error.message,
          duration: 1500
        });
      }
    },

    handleDelete(msg) {
      let fileName = getName(msg.fullPath);
      
      this.$confirm(`确定要删除 "${fileName}" 吗？此操作不可恢复！`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        let token = this.$store.state.token;
        let req = {
          "relative_path": msg.fullPath
        };
        
        fetch(file_delete_path, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(req)
        })
        .then(res => {
          return res.json();
        })
        .then(data => {
          if(data["status"] == "success"){
            this.$message({
              type: 'success',
              message: '删除成功!'
            });
            if(msg.currentDir == this.rootDir){ // 刷新当前工作目录
              this.refreshFileList();
            }
            else{
              this.$store.commit('CHANGE_FOLDER_REFRESH_FLAG', msg.currentDir);
            }
          }
          else{
            this.$message({
              type: 'error',
              message: '删除文件失败'
            });
          }
        })
        .catch(err => {
          console.error('删除文件失败:', err);
          this.$message({
            type: 'error',
            message: '删除文件失败'
          });
        })
      }).catch(() => {
        this.$message({
          type: 'info',
          message: '已取消删除'
        });
      });
    },

    createNewFolder() {
      this.creatingFolder = true;
      this.creatingFile = false;
      this.newFolderName = '';
      this.folderNameError = '';
      this.$nextTick(() => {
        if (this.$refs.folderInput) {
          this.$refs.folderInput.focus();
        }
      });
    },
    
    createNewFile() {
      this.creatingFile = true;
      this.creatingFolder = false;
      this.newFileName = '';
      this.fileNameError = '';
      this.$nextTick(() => {
        if (this.$refs.fileInputName) {
          this.$refs.fileInputName.focus();
        }
      });
    },

    confirmCreateFolder() {
      const folderName = this.newFolderName.trim();
      if (!folderName) {
        this.$message({
          type: 'warning',
          message: '文件夹名称不能为空'
        });
        return;
      }
      
      this.folderNameError = validateFolderName(folderName);
      if (this.folderNameError != '') {
        this.$message({
          type: 'warning',
          message: '文件夹名称不符合规范: ' + this.folderNameError
        });
        return;
      }
      
      this.createFolderAPI(folderName);
      this.cancelCreateFolder();
    },
    
    cancelCreateFolder() {
      this.creatingFolder = false;
      this.newFolderName = '';
      this.folderNameError = '';
    },
    
    confirmCreateFile() {
      const fileName = this.newFileName.trim();
      if (!fileName) {
        this.$message({
          type: 'warning',
          message: '文件名称不能为空'
        });
        return;
      }

      this.fileNameError = validateFileName(fileName);
      if (this.fileNameError != '') {
        this.$message({
          type: 'warning',
          message: '文件名称不符合规范: ' + this.fileNameError
        });
        return;
      }
      
      this.createFileAPI(fileName);
      this.cancelCreateFile();
    },
    
    cancelCreateFile() {
      this.creatingFile = false;
      this.newFileName = '';
      this.fileNameError = '';
    },
    
    createFolderAPI(folderName) {
      const token = this.$store.state.token;
      const req = {
        "relative_dir": this.currentSelectedDir + folderName// 在当前选中的文件夹下创建目录
      };
      
      fetch(file_mkdir_path, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(req)
      })
      .then(res => res.json())
      .then(data => {
        if (data.status === "success") {
          this.$message({
            type: 'success',
            message: '文件夹创建成功'
          });
          if(this.currentSelectedDir == this.rootDir){ // 刷新当前工作目录
            this.refreshFileList();
          }
          else{
            this.$store.commit('CHANGE_FOLDER_REFRESH_FLAG', this.currentSelectedDir); // 刷新当前选中的文件夹
          }
        } else {
          this.$message({
            type: 'error',
            message: data.msg || '文件夹创建失败'
          });
        }
      })
      .catch(err => {
        console.error('创建文件夹失败:', err);
        this.$message({
          type: 'error',
          message: '文件夹创建失败'
        });
      });
    },
    
    createFileAPI(fileName) {
      const token = this.$store.state.token;
      const req = {
        "relative_path": this.currentSelectedDir + fileName + '.md', // 在当前选中的文件夹下创建文件
        "file_content": ""
      };
      
      fetch(file_save_path, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(req)
      })
      .then(res => res.json())
      .then(data => {
        if (data.status === "success") {
          this.$message({
            type: 'success',
            message: '文件创建成功'
          });
          if(this.currentSelectedDir == this.rootDir){ // 刷新当前工作目录
            this.refreshFileList();
          }
          else{
            this.$store.commit('CHANGE_FOLDER_REFRESH_FLAG', this.currentSelectedDir); // 刷新当前选中的文件夹
          }
        } else {
          this.$message({
            type: 'error',
            message: data.msg || '文件创建失败'
          });
        }
      })
      .catch(err => {
        console.error('创建文件失败:', err);
        this.$message({
          type: 'error',
          message: '文件创建失败'
        });
      });
    },
    
    handleBlankAreaClick(event) { // 点击文件树的空白区域, 将选中目录切换回根目录
      // 如果点击的是文件树容器本身或者是填充区域，就认为是点击了空白区域
      if (event.target.classList.contains('file-tree') || 
          event.target.classList.contains('clickable-fill-area') ||
          event.target.classList.contains('empty-state')) {
            this.$store.commit('SET_CURRENT_SELECTED_DIR', this.rootDir);
            this.$store.commit('SET_CURRENT_SELECTED_TYPE', 'dir'); // 当前选中的是文件夹
        // 在这里添加你想要的逻辑
      }
    },
  }
}
</script>

<style scoped>
/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.modal-content {
  background: linear-gradient(145deg, #ffffff, #f8f9fa);
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  width: 420px;
  max-width: 95%;
  animation: modalFadeIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-header {
  padding: 24px 24px 16px;
  display: flex;
  align-items: center;
  background: linear-gradient(120deg, #8a2be2 0%, #9370db 100%);
  color: white;
}

.modal-icon {
  color: rgba(255, 255, 255, 0.9);
  margin-right: 12px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.modal-body {
  padding: 24px;
  background: white;
}

.modal-input {
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 15px;
  box-sizing: border-box;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  background: #f8f9fa;
}

.modal-input:focus {
  outline: none;
  border-color: #8a2be2;
  box-shadow: 0 0 0 3px rgba(138, 43, 226, 0.15);
  background: white;
  transform: translateY(-1px);
}

.modal-input::placeholder {
  color: #adb5bd;
}

.modal-input.error {
  border-color: #dc3545;
}

.modal-footer {
  padding: 16px 24px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: white;
}

.modal-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

.modal-btn span {
  position: relative;
  z-index: 2;
}

.modal-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transform: translateX(-100%);
  transition: transform 0.6s;
}

.modal-btn:hover:not(:disabled)::before {
  transform: translateX(100%);
}

.cancel-btn {
  background: linear-gradient(145deg, #f1f3f5, #e9ecef);
  color: #495057;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.cancel-btn:hover:not(:disabled) {
  background: linear-gradient(145deg, #e9ecef, #dee2e6);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.cancel-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.confirm-btn {
  background: linear-gradient(135deg, #8a2be2 0%, #6a0dad 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(138, 43, 226, 0.3);
}

.confirm-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #7a1bd2 0%, #5a0b9d 100%);
  box-shadow: 0 6px 20px rgba(138, 43, 226, 0.4);
  transform: translateY(-2px);
}

.confirm-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 3px 10px rgba(138, 43, 226, 0.3);
}

/* 错误信息样式 */
.error-message {
  color: #dc3545;
  font-size: 12px;
  margin-top: 8px;
  padding: 6px 12px;
  background-color: rgba(220, 53, 69, 0.1);
  border-radius: 4px;
  border-left: 3px solid #dc3545;
}
</style>